// recorded-draft-mr-push.ts
//
// What the mission journal knows about the Draft-MR push of a candidate: the
// `git_push` intent (draft-merge-request-push.ts) carries `candidate_sha`,
// `pushed_sha` (the commit the MR branch now holds — the candidate minus
// controller paths, never the mission HEAD), `remote`/`branch`/`target`, and
// since round 2 `base_sha` + `recorded_at`; its `operation_result` says
// `pushed`. Two readers use it:
//   - the pre-G3 flow (round 2 M2): while the MR pipeline runs, a re-run reuses
//     the recorded push instead of rebuilding the candidate against a local
//     target branch that may have moved, binding to `pushed_sha` and to the
//     `base_sha` the push was built on;
//   - the approved-G3 leg (release-ci-gate-approved-candidate.ts): which commit
//     the MR's CI must have passed for.
import type { MissionState } from "./mission-state-types.js";

export interface RecordedPush {
  pushedSha: string;
  /** The base the pushed commit was built on; absent in intents written by older CLIs. */
  baseSha?: string;
  /** When the push was recorded (ms since epoch); absent in intents written by older CLIs. */
  recordedAtMs?: number;
  /**
   * `deploy_recipe.recipe_id` the push (its MR trailer) was made under; `null` =
   * none configured then; absent in intents written before round 3.
   */
  deployRecipeId?: string | null;
}

/** Where a release pushes — a recorded push to anywhere else is not "the" push. */
export interface PushDestination {
  remote: string;
  branch: string;
  target: string;
}

function toRecordedPush(params: Record<string, unknown>): RecordedPush {
  const recordedAtMs =
    typeof params.recorded_at === "string" ? Date.parse(params.recorded_at) : Number.NaN;
  const recipe = params.deploy_recipe_id;
  return {
    pushedSha: params.pushed_sha as string,
    ...(typeof params.base_sha === "string" ? { baseSha: params.base_sha } : {}),
    ...(Number.isNaN(recordedAtMs) ? {} : { recordedAtMs }),
    ...(typeof recipe === "string" || recipe === null ? { deployRecipeId: recipe } : {}),
  };
}

/** The newest SUCCESSFUL `git_push` (intent has a `pushed` result) that passes `wanted`. */
function newestSuccessfulPush(
  state: Pick<MissionState, "intents" | "results">,
  wanted: (params: Record<string, unknown>) => boolean,
): RecordedPush | undefined {
  let newest: { seq: number; params: Record<string, unknown> } | undefined;
  for (const intent of Object.values(state.intents)) {
    if (intent.operation_kind !== "git_push") continue;
    if (typeof intent.params.pushed_sha !== "string" || !wanted(intent.params)) continue;
    if (state.results[intent.operation_id]?.outcome !== "pushed") continue;
    if (newest === undefined || intent.seq > newest.seq) {
      newest = { seq: intent.seq, params: intent.params };
    }
  }
  return newest === undefined ? undefined : toRecordedPush(newest.params);
}

const isDestination = (p: Record<string, unknown>, d: PushDestination): boolean =>
  p.remote === d.remote && p.branch === d.branch && p.target === d.target;

/** The destination a release pushes to: its remote + target branch, on the mission's candidate branch. */
export function pushDestinationFor(
  release: { remote: string; target_branch: string },
  candidateBranch: string,
): PushDestination {
  return { remote: release.remote, branch: candidateBranch, target: release.target_branch };
}

/**
 * The newest SUCCESSFUL `git_push` for this candidate (and destination, when
 * given); `undefined` when the journal has none (never pushed, push failed, or
 * the intent has no result yet).
 */
export function findRecordedPush(
  state: Pick<MissionState, "intents" | "results">,
  candidateSha: string,
  destination?: PushDestination,
): RecordedPush | undefined {
  return newestSuccessfulPush(
    state,
    (p) => p.candidate_sha === candidateSha && (!destination || isDestination(p, destination)),
  );
}

/**
 * The newest SUCCESSFUL push to this destination by ANY candidate of the
 * mission (round 3 M-A): what the candidate branch holds if nobody else touched
 * it — the lease a second candidate is pushed with.
 */
export function findLastPushToDestination(
  state: Pick<MissionState, "intents" | "results">,
  destination: PushDestination,
): RecordedPush | undefined {
  return newestSuccessfulPush(state, (p) => isDestination(p, destination));
}
